import { useContext, useEffect, useMemo, useRef, useState } from 'react'
import RGL, { WidthProvider } from 'react-grid-layout'
import 'react-grid-layout/css/styles.css'
import 'react-resizable/css/styles.css'
import { GripVertical, Sparkles, X } from 'lucide-react'
import { GlobalDataContext, TemplateContext, evaluateTemplateValue } from '@ea-lab/reactive-json'
import { useBuilder } from './hooks/useBuilder'
import { BuilderBlockMount } from './BuilderBlockMount'
import { BuilderBlockWrapper } from './BuilderBlockWrapper'
import { BuilderBlockCatalog, DASHBOARD_CATEGORY, SYSTEM_CATEGORY } from './BuilderBlockCatalog'
import { BLOCK_TARGET_EVENT, buildBlockContext } from './chat/blockContext'
import { BlockSettingsMenu, CELL_CHROME_TRIGGER } from './BlockSettingsMenu'
import { peekPendingPlacements, reservedSpotsFor } from './blockPlacement'
import { useChatTurns, liveConversationIds } from './chat/turnStatus'
import { BuilderBlockSkeleton } from './BuilderBlockSkeleton'
import { useCellSettings } from './hooks/useCellSettings'

/**
 * GridLayout — the single 2-D grid ENGINE (Phase 2, lot B). One engine, two
 * presets: this generic one (no base borders/gaps) and `DashboardGridLayout`
 * (a styled wrapper). The grid is a normal builder BLOCK that can be placed on a
 * page or a dashboard.
 *
 * Persistence follows the EXISTING pattern — no new machinery. The child blocks
 * live in this block's data under `cells` (`[{ id, type, data, layout:{x,y,w,h} }]`),
 * exactly like a `list` field holds an array of items. Every mutation (add /
 * remove / move / resize) recomputes that array and writes it wholesale via
 * `updateBlock(gridId, 'cells', cells)` — the SAME call the list editor makes —
 * and both save paths serialize `bloc.data` whole, so `cells` persists for free.
 *
 * Placement (edit mode): a ghost grid materialises the empty cells. Draw a region
 * (press a start cell, drag to an end cell, release) OR click a cell for a
 * width×height popin; either way a wrapper is created at that position/size and
 * the block catalogue opens to pick its content.
 */
const Grid = WidthProvider(RGL)
const num = (v, d) => (Number.isFinite(v) ? v : d)
const uuid = () => (crypto?.randomUUID ? crypto.randomUUID() : `c-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`)
const clamp = (v, lo, hi) => Math.max(lo, Math.min(hi, v))
// Constantes PARTAGÉES : `BuilderBlockMount` mémorise son `dataOverride` sur les
// champs du bloc, et un `dataOverride` neuf RÉ-AMORCE le store du widget. Un
// littéral `[]`/`{}` recréé à chaque rendu de la grille ré-amorçait donc chaque
// cellule en permanence (panneau de filtres qui se referme tout seul, sources de
// données rejouées). Une cellule n'a pas de visibilité par appareil : c'est bien
// une constante.
const NO_HIDDEN = []
const NO_DATA = {}

export const GridLayout = ({ props }) => {
  const globalDataContext = useContext(GlobalDataContext)
  const templateContext = useContext(TemplateContext)
  const resolve = (v) => evaluateTemplateValue({ valueToEvaluate: v, globalDataContext, templateContext })

  const { page, registry, isEditMode, updateBlock, host } = useBuilder()
  const [catalogOpen, setCatalogOpen] = useState(false)
  // Region a new cell will occupy, set by a draw or the dimensions popin; when
  // present, the catalogue's pick lands there instead of appending at the bottom.
  const [pendingRegion, setPendingRegion] = useState(null)
  // Ghost-grid drawing state (start/end cells) and the click popin.
  const [draw, setDraw] = useState(null)
  const [popin, setPopin] = useState(null)

  // This grid IS a top-level bloc; its id lets us read/write page-state.
  const gridId = resolve(props.gridIdLocation ?? '~~.apiData.data.objectData.builderId')
  const bloc = page.blocs.find((b) => b.id === gridId)
  const cells = Array.isArray(bloc?.data?.cells) ? bloc.data.cells : []

  const registryByType = useMemo(() => {
    const m = {}
    for (const e of registry) if (e.type) m[e.type] = e
    return m
  }, [registry])

  // Styling preset (DashboardGridLayout passes these; the generic grid leaves
  // them off). `bordered` gives each cell the DS `.card` chrome.
  const bordered = !!resolve(props.bordered)
  const cols = parseInt(resolve(props.cols), 10) || 12
  const rowHeight = parseInt(resolve(props.rowHeight), 10) || 34
  const gap = Number.isFinite(resolve(props.gap)) ? resolve(props.gap) : (bordered ? 24 : 0)
  const MARGIN = [gap, gap]

  const setCells = (next) => { if (gridId) updateBlock(gridId, 'cells', next) }

  const layout = cells.map((c, i) => {
    const p = c.layout || {}
    return { i: String(i), x: num(p.x, (i * 6) % cols), y: num(p.y, Math.floor(i / 2) * 10), w: num(p.w, 6), h: num(p.h, 9), minW: 2, minH: 2 }
  })

  const applyLayout = (l) => {
    const byId = {}
    l.forEach((it) => { byId[it.i] = it })
    let changed = false
    const next = cells.map((c, i) => {
      const it = byId[String(i)]
      if (!it) return c
      const cur = c.layout || {}
      if (cur.x === it.x && cur.y === it.y && cur.w === it.w && cur.h === it.h) return c
      changed = true
      return { ...c, layout: { x: it.x, y: it.y, w: it.w, h: it.h } }
    })
    if (changed) setCells(next)
  }

  const removeCell = (i) => setCells(cells.filter((_, idx) => idx !== i))
  // Fold a nested block's field edit back into its cell (lot B increment 2).
  const foldIntoCell = (i, field, value) =>
    setCells(cells.map((c, idx) => (idx === i ? { ...c, data: { ...c.data, [field]: value } } : c)))

  // Create a cell at an explicit region (from a draw / popin) and pick content.
  const addCellAt = (region, blockDef) => {
    const w = clamp(num(region.w, 6), 2, cols)
    const x = clamp(num(region.x, 0), 0, cols - w)
    const cell = { id: uuid(), type: blockDef.type, data: { ...(blockDef.defaultData || {}) }, layout: { x, y: num(region.y, 0), w, h: Math.max(2, num(region.h, 9)) } }
    setCells([...cells, cell])
    setCatalogOpen(false)
    setPendingRegion(null)
  }
  // Safety fallback if the catalogue is ever opened without a target region:
  // append below existing content.
  const addCellAppend = (blockDef) => {
    const nextY = cells.reduce((m, c) => Math.max(m, num(c.layout?.y, 0) + num(c.layout?.h, 9)), 0)
    addCellAt({ x: 0, y: nextY, w: 6, h: 9 }, blockDef)
  }

  // The assistant path places a cell MUCH later than the click that armed it, so
  // it must not write through a closure that captured `cells` at catalogue time:
  // the grid may well have been edited meanwhile, and those edits would be lost.
  // Mirror kept current during render, read at placement time.
  const cellsRef = useRef(cells)
  cellsRef.current = cells

  // La RÉGION, elle, se capture au contraire à l'armement — c'est justement ce
  // que l'éditeur a désigné. Elle vivait dans un ref partagé, écrasé par la zone
  // suivante : décrire une deuxième zone pendant que la première se fabriquait
  // faisait atterrir les deux blocs au même endroit. Une fabrique de placeur par
  // demande, et chacune garde la sienne.
  const makeCellPlacer = (region) => (blockDef) => {
    // Mise à jour FONCTIONNELLE : deux zones décrites de front aboutissent l'une
    // après l'autre, et la seconde doit se ranger à côté de la première. Un
    // tableau calculé depuis le miroir de rendu l'écraserait si les deux
    // réponses tombaient de près.
    setCells((prev) => {
      const current = Array.isArray(prev) ? prev : cellsRef.current
      const target = region ?? {
        x: 0,
        y: current.reduce((m, c) => Math.max(m, num(c.layout?.y, 0) + num(c.layout?.h, 9)), 0),
        w: 6,
        h: 9,
      }
      const w = clamp(num(target.w, 6), 2, cols)
      const x = clamp(num(target.x, 0), 0, cols - w)
      return [...current, {
        id: uuid(),
        type: blockDef.type,
        data: { ...(blockDef.defaultData || {}) },
        layout: { x, y: num(target.y, 0), w, h: Math.max(2, num(target.h, 9)) },
      }]
    })
  }

  // Réglages avancés d'un widget de cellule : même menu, même pop-in ancrée que
  // pour un bloc de premier niveau, branchés sur `foldIntoCell`.
  const { openCellSetting, cellSettingPopover } = useCellSettings()

  // A cell widget has no standard block toolbar (its wrapper is `nested`), so its
  // ✨ lives in the cell chrome — same gesture, same pinned-edit context as a
  // top-level block.
  const askAiForCell = (cell, i) => {
    const def = registryByType[cell.type]
    if (!def?.defNid) return
    window.dispatchEvent(new CustomEvent(BLOCK_TARGET_EVENT, {
      detail: buildBlockContext({
        bloc: { id: cell.id, type: cell.type, data: cell.data ?? {} },
        def,
        index: i,
        page,
        host: { kind: 'dashboard' },
      }),
    }))
  }

  // Les zones que l'assistant est en train de remplir SUR CETTE GRILLE : une par
  // demande partie dont le tour tourne encore. C'est le pendant, ici, du carré
  // d'attente que le canevas dessine entre deux blocs d'une page — sans lui,
  // décrire une zone laissait la grille absolument identique pendant toute la
  // fabrication, et rien ne disait qu'une deuxième zone pouvait être décrite
  // dans la foulée.
  const chatTurns = useChatTurns()
  const reservedRegions = useMemo(() => {
    if (!isEditMode || !gridId) return []
    const live = liveConversationIds(chatTurns)
    return reservedSpotsFor(peekPendingPlacements(), live, spot => spot?.kind === 'grid' && spot.gridId === gridId)
      .map(spot => spot.region)
      .filter(r => r && Number.isFinite(num(r.w, NaN)) && Number.isFinite(num(r.h, NaN)))
  }, [chatTurns, isEditMode, gridId])

  // ── Ghost grid geometry ────────────────────────────────────────────────────
  // Occupied positions + how many rows to materialise (content + a few empties).
  const occupied = useMemo(() => {
    const set = new Set()
    // Les cases d'un bloc posé, ET celles d'une zone que l'assistant est en
    // train de remplir : sans les secondes, on redessinerait par-dessus une
    // place déjà promise, et deux blocs atterriraient l'un sur l'autre.
    for (const c of [...cells.map(c => c.layout || {}), ...reservedRegions]) {
      for (let dx = 0; dx < num(c.w, 6); dx++) {
        for (let dy = 0; dy < num(c.h, 9); dy++) set.add(`${num(c.x, 0) + dx},${num(c.y, 0) + dy}`)
      }
    }
    return set
  }, [cells, reservedRegions])
  const contentBottom = cells.reduce((m, c) => Math.max(m, num(c.layout?.y, 0) + num(c.layout?.h, 9)), 0)
  const gridRows = Math.max(contentBottom + 4, 8)

  // Normalised rectangle of the current draw (or null).
  const drawRect = draw ? {
    minX: Math.min(draw.a.x, draw.b.x), maxX: Math.max(draw.a.x, draw.b.x),
    minY: Math.min(draw.a.y, draw.b.y), maxY: Math.max(draw.a.y, draw.b.y),
  } : null

  // Finish a draw on mouse release: a real drag opens the catalogue on that
  // region; a plain click opens the width×height popin.
  useEffect(() => {
    if (!draw) return
    const finish = () => {
      const r = {
        minX: Math.min(draw.a.x, draw.b.x), maxX: Math.max(draw.a.x, draw.b.x),
        minY: Math.min(draw.a.y, draw.b.y), maxY: Math.max(draw.a.y, draw.b.y),
      }
      const w = r.maxX - r.minX + 1
      const h = r.maxY - r.minY + 1
      if (w === 1 && h === 1) {
        setPopin({ x: r.minX, y: r.minY, w: 4, h: 4, rect: draw.rect })
      }
      else {
        setPendingRegion({ x: r.minX, y: r.minY, w, h })
        setCatalogOpen(true)
      }
      setDraw(null)
    }
    window.addEventListener('mouseup', finish)
    return () => window.removeEventListener('mouseup', finish)
  }, [draw])

  const ghostCells = []
  if (isEditMode) {
    for (let y = 0; y < gridRows; y++) {
      for (let x = 0; x < cols; x++) {
        if (occupied.has(`${x},${y}`)) continue
        const inDraw = drawRect && x >= drawRect.minX && x <= drawRect.maxX && y >= drawRect.minY && y <= drawRect.maxY
        ghostCells.push(
          <div
            key={`${x},${y}`}
            role="button"
            tabIndex={-1}
            aria-label={`Ajouter un bloc en colonne ${x + 1}, ligne ${y + 1}`}
            onMouseDown={(e) => { e.preventDefault(); setDraw({ a: { x, y }, b: { x, y }, rect: e.currentTarget.getBoundingClientRect() }) }}
            onMouseEnter={() => { if (draw) setDraw((d) => (d ? { ...d, b: { x, y } } : d)) }}
            style={{ gridColumn: x + 1, gridRow: y + 1, pointerEvents: 'auto' }}
            className={`rounded-[3px] border border-dashed transition-colors cursor-crosshair ${
              inDraw ? 'border-teal-500 bg-teal-400/25' : 'border-gray-300 bg-gray-50/40 hover:border-teal-400 hover:bg-teal-50/70'
            }`}
          />
        )
      }
    }
  }

  return (
    <div className="relative">
      {isEditMode && (
        <p className="mb-1.5 text-[11px] text-gray-400">
          Dessinez une zone sur la grille (glisser) ou cliquez une case pour placer un bloc.
        </p>
      )}

      {/* Grid + ghost overlay share one positioning context. minHeight keeps the
          empty ghost rows visible below the content. */}
      {/* `isolate` : les couches internes (calque fantôme en z-30, grille en
          z-20, chrome de cellule en z-30) vivaient dans le contexte
          d'empilement RACINE, à égalité avec la barre d'outils `sticky` du
          builder (z-30) — et plus bas dans le DOM, donc elles peignaient
          PAR-DESSUS elle au défilement. Isolées ici, elles gardent leur ordre
          entre elles sans jamais remonter au-dessus du chrome de l'éditeur. */}
      <div className="relative isolate" style={{ minHeight: isEditMode ? gridRows * (rowHeight + gap) + gap : undefined }}>
        {isEditMode && (
          <div
            aria-hidden={ghostCells.length === 0}
            className="absolute inset-0 z-30"
            style={{
              display: 'grid',
              gridTemplateColumns: `repeat(${cols}, 1fr)`,
              gridAutoRows: `${rowHeight}px`,
              gap: `${gap}px`,
              padding: `${gap}px`,
              pointerEvents: 'none',
            }}
          >
            {ghostCells}
            {/* La place réservée, à l'endroit exact de la zone dessinée. Dans le
                calque fantôme (`pointerEvents: none`) : il n'y a rien à y faire
                tant que le bloc n'est pas là. */}
            {reservedRegions.map((r, n) => (
              <div
                key={`reserved-${n}`}
                style={{
                  gridColumn: `${num(r.x, 0) + 1} / span ${clamp(num(r.w, 6), 1, cols)}`,
                  gridRow: `${num(r.y, 0) + 1} / span ${Math.max(1, num(r.h, 9))}`,
                }}
              >
                <BuilderBlockSkeleton fit />
              </div>
            ))}
          </div>
        )}

        <Grid
          className="layout relative z-20"
          layout={layout}
          cols={cols}
          rowHeight={rowHeight}
          margin={MARGIN}
          isDraggable={isEditMode}
          isResizable={isEditMode}
          draggableHandle=".grid-cell-handle"
          resizeHandles={['se']}
          compactType={null}
          preventCollision
          onDragStop={applyLayout}
          onResizeStop={applyLayout}
        >
          {cells.map((c, i) => {
            const def = registryByType[c.type]
            return (
              <div key={String(i)} className={`relative overflow-hidden ${bordered ? 'card flex flex-col' : ''}`}>
                {isEditMode && (
                  <div className="absolute top-1.5 left-1.5 right-1.5 z-30 flex items-center justify-between pointer-events-none">
                    <span className="grid-cell-handle pointer-events-auto flex items-center justify-center w-6 h-6 rounded-md bg-white/90 border border-gray-200 text-gray-400 hover:text-gray-700 cursor-move shadow-sm" title="Déplacer" aria-hidden="true">
                      <GripVertical size={14} />
                    </span>
                    <span className="flex items-center gap-1.5">
                      {def?.settings?.length > 0 && (
                        <BlockSettingsMenu
                          settings={def.settings}
                          triggerClassName={CELL_CHROME_TRIGGER}
                          onPick={(decl, anchorEl) => openCellSetting(decl, anchorEl, {
                            value: (c.data ?? {})[decl.field] ?? def.defaultData?.[decl.field],
                            commit: (key, value) => foldIntoCell(i, key, value),
                          })}
                        />
                      )}
                      {registryByType[c.type]?.defNid && (
                        <button
                          type="button"
                          onClick={() => askAiForCell(c, i)}
                          className="pointer-events-auto flex items-center justify-center w-6 h-6 rounded-md bg-white/90 border border-gray-200 text-gray-400 hover:text-teal-700 shadow-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-teal-500"
                          aria-label={`Modifier le bloc ${registryByType[c.type]?.name ?? c.type} avec l'assistant IA`}
                          title="Modifier avec l'IA"
                        >
                          <Sparkles size={14} aria-hidden="true" />
                        </button>
                      )}
                      <button type="button" onClick={() => removeCell(i)} className="pointer-events-auto flex items-center justify-center w-6 h-6 rounded-md bg-white/90 border border-gray-200 text-gray-400 hover:text-red-600 shadow-sm" title="Retirer">
                        <X size={14} />
                      </button>
                    </span>
                  </div>
                )}
                {def?.rjBuild ? (
                  <BuilderBlockWrapper
                    bloc={{ id: c.id, type: c.type, data: c.data ?? NO_DATA, hiddenOn: NO_HIDDEN }}
                    index={i}
                    total={cells.length}
                    nested
                    onCommitField={(field, value) => foldIntoCell(i, field, value)}
                  >
                    {/* `key` sur la version de la déf : `ReactiveJsonRoot` NE
                        rejoue PAS son rjBuild quand la prop change (vérifié) —
                        seul un remontage prend la nouvelle définition. Sans ça,
                        un widget réécrit par l'assistant (✨ de la cellule)
                        restait à l'ancienne version jusqu'au F5. Même clé que
                        les blocs de premier niveau (BuilderCanvas / BuilderGridLayout). */}
                    <BuilderBlockMount key={def.version} bloc={{ id: c.id, type: c.type, data: c.data ?? NO_DATA, hiddenOn: NO_HIDDEN }} def={def} />
                  </BuilderBlockWrapper>
                ) : (
                  <div className="p-4 text-xs text-red-500 bg-red-50 border border-red-200 rounded">
                    Bloc inconnu : <code>{c.type}</code>
                  </div>
                )}
              </div>
            )
          })}
        </Grid>
      </div>

      {popin && (
        <DimensionsPopin
          popin={popin}
          cols={cols}
          onCancel={() => setPopin(null)}
          onConfirm={({ w, h }) => { setPendingRegion({ x: popin.x, y: popin.y, w, h }); setPopin(null); setCatalogOpen(true) }}
        />
      )}

      {catalogOpen && (
        <BuilderBlockCatalog
          position={0}
          // Ce que prend une CASE dépend de l'hôte, comme le catalogue de premier
          // niveau : sur un tableau de bord, ses widgets ; sur une page, les blocs
          // de la page. La même grille sert les deux (elle se pose aussi sur une
          // page, par « Grille » au liseré d'insertion) — n'offrir que la
          // catégorie « Dashboard » y donnait une grille impossible à remplir.
          // Dans les deux cas, jamais une grille : pas de grille dans la grille.
          accept={(def) => def.type !== 'DashboardGridLayout' && (
            host
              ? def.category === DASHBOARD_CATEGORY
              : def.category !== DASHBOARD_CATEGORY && def.category !== SYSTEM_CATEGORY
          )}
          onClose={() => { setCatalogOpen(false); setPendingRegion(null) }}
          onSelect={(def) => (pendingRegion ? addCellAt(pendingRegion, def) : addCellAppend(def))}
          // Where a block the assistant creates during this turn must land. Not
          // `onSelect`: that one closes over the cells as they are NOW, and the
          // turn ends much later.
          onPlace={makeCellPlacer(pendingRegion)}
          // Et OÙ l'attente se voit : dans la zone que l'éditeur vient de
          // dessiner, sur cette grille-ci. Sans cette indication, le catalogue
          // retombait sur la place d'insertion d'une PAGE et le carré s'allumait
          // en tête de page — loin de la grille, qui elle ne montrait rien.
          placementSpot={{ kind: 'grid', gridId, region: pendingRegion }}
        />
      )}

      {cellSettingPopover}
    </div>
  )
}

/**
 * Small width×height popin shown when a grid cell is clicked (not dragged),
 * anchored near the clicked cell. Confirm opens the block catalogue.
 */
const DimensionsPopin = ({ popin, cols, onCancel, onConfirm }) => {
  const [w, setW] = useState(popin.w)
  const [h, setH] = useState(popin.h)
  const maxW = cols - popin.x
  const top = (popin.rect?.bottom ?? 0) + 6
  const left = popin.rect?.left ?? 0
  return (
    <>
      <div className="fixed inset-0 z-40" onMouseDown={onCancel} />
      <div className="fixed z-50 w-56 rounded-xl border border-gray-200 bg-white shadow-xl p-3" style={{ top, left }} onMouseDown={(e) => e.stopPropagation()}>
        <p className="text-xs font-semibold text-gray-700 mb-2">Dimensions du bloc</p>
        <div className="flex items-center gap-2">
          <label className="flex-1 text-xs text-gray-500">
            Largeur
            <input type="number" min={2} max={maxW} value={w} onChange={(e) => setW(clamp(parseInt(e.target.value, 10) || 2, 2, maxW))}
              className="mt-0.5 w-full px-2 py-1 text-sm border border-gray-200 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500" />
          </label>
          <label className="flex-1 text-xs text-gray-500">
            Hauteur
            <input type="number" min={2} value={h} onChange={(e) => setH(Math.max(2, parseInt(e.target.value, 10) || 2))}
              className="mt-0.5 w-full px-2 py-1 text-sm border border-gray-200 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500" />
          </label>
        </div>
        <div className="flex justify-end gap-2 mt-3">
          <button type="button" onClick={onCancel} className="px-2.5 py-1 text-xs rounded-md text-gray-500 hover:bg-gray-100">Annuler</button>
          <button type="button" onClick={() => onConfirm({ w: clamp(w, 2, maxW), h: Math.max(2, h) })} className="px-2.5 py-1 text-xs rounded-md bg-teal-700 text-white hover:bg-teal-800">Choisir le contenu</button>
        </div>
      </div>
    </>
  )
}
